import React from "react";
import {
  Box,
  Chip,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Replay } from "@mui/icons-material";

// Shared look & helpers for the customer screens (dashboard, My Orders,
// self-order).

export const ACCENT = "#0D7377";
export const ACCENT_DARK = "#0A5C60";
export const ACCENT_SOFT = "#E7F5F5";
export const PAGE_BG = "#F4F6FA";
export const NO_IMAGE =
  "https://dummyimage.com/150x150/eeeeee/aaaaaa&text=No+Photo";

// Clears the fixed bottom tab bar (60px) with breathing room.
export const FOOTER_SPACE = "calc(96px + env(safe-area-inset-bottom))";

// The normal life of an order.
export const STAGES = ["New", "Issued", "In Process", "Completed", "Delivered"];

export const parseImages = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [value];
  }
};

export const isVideoUrl = (url) => /\.(mp4|mov|wmv|avi|mkv)$/i.test(url);

export const firstImage = (order) =>
  parseImages(order.order_images).find((url) => !isVideoUrl(url)) || NO_IMAGE;

// YYYY-MM-DD... -> DD-MM-YYYY
export const formatDate = (value) =>
  value ? String(value).slice(0, 10).split("-").reverse().join("-") : "";

export const formatWeight = (value) =>
  Number(value) > 0 ? `${Number(value)} g` : "";

export const isRejected = (status) => /reject/i.test(status || "");

export const sortNewestFirst = (orders) =>
  [...orders].sort(
    (a, b) =>
      String(b.orderdate).localeCompare(String(a.orderdate)) ||
      b.orderid - a.orderid,
  );

export const StatusChip = ({ status, size = "small" }) => {
  const rejected = isRejected(status);
  const delivered = status === "Delivered";
  return (
    <Chip
      label={status || "Unknown"}
      size={size}
      sx={{
        fontWeight: 600,
        height: size === "small" ? 22 : undefined,
        fontSize: size === "small" ? 11 : undefined,
        bgcolor: rejected ? "#fdecea" : delivered ? "#e8f5e9" : ACCENT_SOFT,
        color: rejected ? "#c62828" : delivered ? "#2e7d32" : ACCENT,
      }}
    />
  );
};

// Slim 5-segment progress bar; labels only where asked (detail view).
export const StageBar = ({ status, showLabels = false }) => {
  const current = STAGES.indexOf(status);
  if (current === -1) return null;

  return (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {STAGES.map((stage, index) => (
        <Box key={stage} sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              height: 4,
              borderRadius: "8px",
              bgcolor: index <= current ? ACCENT : "#E4E7EC",
            }}
          />
          {showLabels && (
            <Typography
              noWrap
              sx={{
                fontSize: 10,
                mt: 0.5,
                color: index === current ? ACCENT : "text.secondary",
                fontWeight: index === current ? 700 : 400,
              }}
            >
              {stage}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

// Where the order is, in one short line.
export const progressText = (order) => {
  const status = order.work_status;
  if (status === "Delivered") {
    const on = formatDate(order.delivereddate || order.work_status_date);
    return on ? `Delivered on ${on}` : "Delivered";
  }
  if (isRejected(status)) return "Rejected";
  const due = formatDate(order.deliverydate);
  return due ? `Due ${due}` : "";
};

const Thumb = ({ order, size }) => (
  <Box
    component="img"
    src={firstImage(order)}
    alt={order.storeorderno}
    loading="lazy"
    onError={(e) => (e.target.src = NO_IMAGE)}
    sx={{
      width: size,
      height: size,
      borderRadius: "6px",
      objectFit: "cover",
      flexShrink: 0,
      display: "block",
      bgcolor: "#F2F4F7",
      border: "1px solid #E4E7EC",
    }}
  />
);

const ReorderButton = ({ order, onReorder }) => (
  <Button
    size="small"
    variant="outlined"
    startIcon={<Replay sx={{ fontSize: "16px !important" }} />}
    onClick={(e) => {
      e.stopPropagation();
      onReorder(order);
    }}
    sx={{
      flexShrink: 0,
      color: ACCENT,
      borderColor: "#D0D5DD",
      bgcolor: "#fff",
      "&:hover": { borderColor: ACCENT, bgcolor: ACCENT_SOFT },
    }}
  >
    Reorder
  </Button>
);

// Phone: one compact row per order, rows grouped in a single card.
const OrderRow = ({ order, onOpen, onReorder, divider }) => {
  const line2 = [formatDate(order.orderdate), order.itemtype_name]
    .filter(Boolean)
    .join(" · ");
  const line3 = [
    formatWeight(order.weight),
    `${order.pcs || 1} pc`,
    progressText(order),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box
      onClick={() => onOpen?.(order)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 1.5,
        py: 1.25,
        borderTop: divider ? "1px solid #EEF0F3" : "none",
        cursor: onOpen ? "pointer" : "default",
        "&:active": { bgcolor: "#F9FAFB" },
      }}
    >
      <Thumb order={order} size={44} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography fontWeight={600} fontSize={14.5} lineHeight={1.3} noWrap>
          {order.storeorderno}
        </Typography>
        <Typography fontSize={12.5} color="text.secondary" noWrap>
          {line2}
        </Typography>
        <Typography fontSize={12.5} color="text.secondary" noWrap>
          {line3}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 0.5,
          flexShrink: 0,
        }}
      >
        <StatusChip status={order.work_status} />
        {onReorder && (
          <Button
            size="small"
            startIcon={<Replay sx={{ fontSize: "15px !important" }} />}
            onClick={(e) => {
              e.stopPropagation();
              onReorder(order);
            }}
            sx={{
              color: ACCENT,
              fontSize: 12.5,
              fontWeight: 600,
              minWidth: 0,
              px: 0.75,
              py: 0.25,
              mr: -0.75,
              "& .MuiButton-startIcon": { mr: 0.5 },
            }}
          >
            Reorder
          </Button>
        )}
      </Box>
    </Box>
  );
};

const COLUMNS = [
  "Photo",
  "Order no.",
  "Order date",
  "Item",
  "Gross wt",
  "Pcs",
  "Delivery",
  "Status",
  "",
];

// Orders as a table on tablet/desktop, compact cards on phones.
export const OrdersList = ({ orders, onOpen, onReorder }) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("md"));

  if (isPhone) {
    return (
      <Paper
        variant="outlined"
        sx={{ borderRadius: "10px", overflow: "hidden" }}
      >
        {orders.map((order, index) => (
          <OrderRow
            key={order.orderid}
            order={order}
            onOpen={onOpen}
            onReorder={onReorder}
            divider={index > 0}
          />
        ))}
      </Paper>
    );
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ borderRadius: "10px", overflow: "hidden" }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            {COLUMNS.map((label, i) => (
              <TableCell
                key={i}
                align={i === COLUMNS.length - 1 ? "right" : "left"}
                sx={{ py: 1.25, whiteSpace: "nowrap" }}
              >
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((order) => (
            <TableRow
              key={order.orderid}
              hover
              onClick={() => onOpen?.(order)}
              sx={{
                cursor: onOpen ? "pointer" : "default",
                "&:last-child td": { borderBottom: 0 },
                "& td": { py: 1.25 },
              }}
            >
              <TableCell sx={{ width: 64 }}>
                <Thumb order={order} size={44} />
              </TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                {order.storeorderno}
              </TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                {formatDate(order.orderdate)}
              </TableCell>
              <TableCell sx={{ color: "text.secondary" }}>
                {order.itemtype_name || "—"}
              </TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                {formatWeight(order.weight) || "—"}
              </TableCell>
              <TableCell>{order.pcs || 1}</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>
                {progressText(order) || "—"}
              </TableCell>
              <TableCell sx={{ minWidth: 150 }}>
                <StatusChip status={order.work_status} />
                {order.work_status !== "Delivered" &&
                  !isRejected(order.work_status) && (
                    <Box sx={{ mt: 0.75, maxWidth: 120 }}>
                      <StageBar status={order.work_status} />
                    </Box>
                  )}
              </TableCell>
              <TableCell align="right" sx={{ width: 120 }}>
                {onReorder && (
                  <ReorderButton order={order} onReorder={onReorder} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
